import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./QuotationInvoice.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationInfoType } from "../Quotation.types";
import { QuotationContext } from "../QuotationContext";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { useNavigate } from "react-router-dom";
import { priceWithCurrency, priceWithCurrencyForJapan, symbolForCurrency } from "../../../Utils/PriceWithCurrency";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import { formattedPrice, formattedPriceForJapan } from "../../../Utils/FormattedPrice";
import { JapanQuotationTemplate } from "./JapanQuotationTemplate";
import { GenericQuotationTemplate } from "./GenericQuotationTemplate";
import { formatDateWithPattern, getCountryDateFormat } from "../../../Utils/DateFormatUtility";
import { useLocalStorageContext } from "../../../Provider/LocalStorageProvider";
import { hasWarrantyItems } from "../../../Utils/QuotationUtils";

// Helper functions for Japan-specific formatting
const getFormattedPrice = (price: string | number, country: string): string => {
  return country === "Japan" ? formattedPriceForJapan(price) : formattedPrice(price);
};

const DOMESTIC_REVERSE_CHARGE_COUNTRIES = ["Germany", "Austria", "Netherlands", "Denmark", "Spain", "Italy", "Portugal", "Sweden"];

const getPriceWithCurrency = (currency: string, price: string | number, country: string): string => {
  return country === "Japan" ? priceWithCurrencyForJapan(currency, price) : priceWithCurrency(currency, price);
};

// Helper function for country-specific date formatting
const formatDateForCountry = async (dateStr: string | undefined, countryName: string | undefined): Promise<string> => {
  if (!dateStr) return '';
  
  try {
    // Parse the date string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || '';
    }
    
    // Get country's preferred date format
    let dateFormat = 'DD/MM/YYYY'; // Default format
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }
    
    // Format the date according to country preference
    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr || '';
  }
};

export const QuotationInvoice: React.FunctionComponent = () => {
  const navigate = useNavigate();
  // Use a variable for Singapore country name to ensure case-sensitive comparison
  const SINGAPORE_COUNTRY = "Singapore";
  const appInsights = useAppInsightsContext();
  const { selectedLanguage } = useLocalStorageContext();
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(
    new Uint8Array()
  );
  const [countryProfileInfo, setCountryProfileInfo] =
    useState<CountryProfileInfo>();
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] =
    useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  const columnsNumber = quotationInfo?.customer?.region === "EMEA" ? 9 : 8;
  const customerPrimaryContactName =
    quotationInfo?.customer?.primaryContact?.trim() ||
    `${quotationInfo?.customer?.authorizedBuyerFirstName || ""} ${quotationInfo?.customer?.authorizedBuyerLastName || ""}`.trim() ||
    `${quotationInfo?.customer?.selectedBillToContact?.contactFirstName || ""} ${quotationInfo?.customer?.selectedBillToContact?.contactLastName || ""}`.trim() ||
    "";
  
  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] = useState<string>('');
  const [formattedExpiryDate, setFormattedExpiryDate] = useState<string>('');
  const [formattedCurrentDate, setFormattedCurrentDate] = useState<string>('');

  // Format dates according to customer's country preference
  useEffect(() => {
    const formatDates = async () => {
      try {
        const countryName = quotationInfo?.customer?.country;
        
        // Format quotation date
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(quotationInfo.quotationDate, countryName);
          setFormattedQuotationDate(formatted);
        }
        
        // Format expiry date
        if (quotationInfo?.expiryDate) {
          const formatted = await formatDateForCountry(quotationInfo.expiryDate, countryName);
          setFormattedExpiryDate(formatted);
        }
        
        // Format current date
        const formatted = await formatDateForCountry(new Date().toISOString(), countryName);
        setFormattedCurrentDate(formatted);
      } catch (error) {
        console.error('Error formatting dates:', error);
      }
    };

    formatDates();
  }, []);

  const pdfFileName = `${quotationInfo?.quotationNumber}_Quotation`;

  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([
        ...quotationInfo?.standaloneItems,
        ...quotationInfo?.bundleItems
      ] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([
          ...quotationInfo?.standaloneItems,
          ...quotationInfo.bundleItems,
        ] as IExtendedDetailItemEntity[])
        : [];
  
  // Check if cart contains warranty items
  const [hasWarranty, setHasWarranty] = useState(false);

  useEffect(() => {
    setHasWarranty(hasWarrantyItems(lineItems));
  }, [lineItems]);

  // Memoized domestic reverse charges logic
  const showDomesticReverseCharges = useMemo(() => {
    const country = quotationInfo?.country;
    const isInDRCCountry = DOMESTIC_REVERSE_CHARGE_COUNTRIES.includes(country);
    const hasTaxExemptFalse = quotationInfo?.customer?.taxExempt === false;
    const hasValidVatId = quotationInfo?.customer?.taxId && quotationInfo?.customer?.taxId.trim() !== "";
    const isTotalTaxZero = Number(quotationInfo?.totalTax) === 0;
    const subtotal = quotationInfo?.subTotal !== undefined && quotationInfo?.subTotal !== null ? Number(quotationInfo.subTotal) : null;
    
    // Base conditions must be met
    if (!isInDRCCountry || !hasTaxExemptFalse || !hasValidVatId || subtotal === null) {
      return false;
    }
    
    // Check if any product contains specific keywords
    // Keywords ordered from most specific to least specific to avoid incorrect matches
    const surfaceKeywords = country === "Germany"
    ? ['xbox', 'surface pro x', 'surface pro', 'surface go', 'surface duo']
    : ['xbox', 'surface pro x', 'surface pro', 'surface go', 'surface laptop', 'surface book', 'surface duo'];
    
    // Helper function to check if a product matches any surface keyword
    const matchesSurfaceKeyword = (productName: string): boolean => {
      return surfaceKeywords.some(keyword => {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        return regex.test(productName);
      });
    };
    
    const hasQualifyingProduct = lineItems.some(item => {
      const productName = item.productTitle || '';
      const hasKeyword = matchesSurfaceKeyword(productName);
      const hasVat = item.lineItemTax && Number(item.lineItemTax) > 0;
      return hasKeyword && !hasVat;
    });
    
    // Check if any other product (non-surface keywords) has lineItemTax > 0
    const hasOtherProductWithVat = lineItems.some(item => {
      const productName = item.productTitle || '';
      const hasKeyword = matchesSurfaceKeyword(productName);
      const hasVat = item.lineItemTax && Number(item.lineItemTax) > 0;
      return !hasKeyword && !hasVat; // Other products (not in keywords) with VAT
    });
    
    // Country-specific rules
    switch (country) {
      case "Austria":
        // Austria: (surfaceKeywords OR other products with VAT) AND subtotal >= EUR 5000
        return (isTotalTaxZero || hasQualifyingProduct || hasOtherProductWithVat) && subtotal >= 5000;
      
      case "Germany": 
        // Germany: show DRC note when VAT is effectively not applied OR qualifying products are present, and subtotal is at least EUR 5000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 5000;
      
      case "Netherlands":
        // Netherlands: existing conditions AND subtotal >= EUR 10000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 10000;
      
      case "Denmark":
        // Denmark: existing conditions (no subtotal threshold)
        return isTotalTaxZero || hasQualifyingProduct;
      
      case "Spain":
        // Spain: existing conditions AND subtotal >= EUR 10000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 10000;
      
      case "Sweden":
        // Sweden: existing conditions AND subtotal >= SEK 100000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 100000;
      
      case "Italy":
      case "Portugal":
        // Other DRC countries: existing conditions
        return isTotalTaxZero || hasQualifyingProduct;
      
      default:
        return false;
    }
  }, [lineItems, quotationInfo?.country, quotationInfo?.customer?.taxExempt, quotationInfo?.customer?.taxId, quotationInfo?.totalTax, quotationInfo?.subTotal]);
  
  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: "In QuotationInvoice.tsx, Clicked Print button to download the Quote Preview",
    });
    if (
      termsAndConditionsDocument.byteLength === 0 &&
      termsAndConditionsDocumentId
    ) {
      await CountryProfileService.getCountryProfileDocument(
        termsAndConditionsDocumentId
      ).then((res) => {
        appInsights.trackEvent({
          name: "In QuotationInvoice.tsx, Country Terms and Condition document fetched successfully.",
        });
        if (res.data === null) {
          makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        }
        settermsAndConditionsDocument(res.data);
      })
      .catch((error)=>{
        makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        appInsights.trackException({
          error,
          properties: {
            name: `In DPRProforma.tsx, error while fetching termsAndCondition document.`,
          },
        });
      });     
    } else {
      makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
    }
  };
  const backButtonClick = async () => {
    appInsights.trackEvent({
      name: "In QuotationInvoice.tsx, Clicked Back button.",
      properties: { quotationId: quotationInfo.quotationId },
    });
    navigate("/QuotationDetail", {
      state: {
        searchText: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
      },
    });
  };
  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);
  useEffect(() => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(quotationInfo.country)
      .then(async (res) => {
        if (res !== undefined) {
          settermsAndConditionsDocumentId(
            res.data.data.data.termsAndConditions[
              res.data.data.data.termsAndConditions?.length - 1
            ]?.id
          );
          setCountryProfileInfo(res.data.data.data);
          appInsights.trackEvent({
            name: "In QuotationInvoice.tsx, country detail fetched successfully.",
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: "In QuotationInvoice.tsx, error while getting all country detail.",
          },
        });
      });
  }, []);

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  // Use Japanese template for Japan quotes only when Japanese language is selected
  // Read directly from localStorage to get the most up-to-date language value
  // This fixes the issue where context might not have updated yet when navigating from search
  const currentLanguageFromStorage = localStorage.getItem("selectedLanguage") || selectedLanguage;
  if (quotationInfo?.country === "Japan" && currentLanguageFromStorage === "ja") {
    return <JapanQuotationTemplate />;
  }

  // Use Generic template for all countries with language switching support
  // This template now supports all countries with localization
  // Pass currentLanguageFromStorage to ensure the most up-to-date language is used
  if (quotationInfo?.country) {
    return <GenericQuotationTemplate country={quotationInfo.country} selectedLanguage={currentLanguageFromStorage} />;
  }

  return (
    <div>
      {quotationInfo ? (
        <>
          <div className="top30">
            <PrimaryButton className="float-right" onClick={PrintHtmlToPdf}>
              Generate quote PDF
            </PrimaryButton>
            <PrimaryButton className="float-right" onClick={backButtonClick}>
              Back
            </PrimaryButton>
          </div>
          <div style={{ padding: "40px 40px" }}>
            <div id={pdfFileName}  >
              <div className={`flex space-between  ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'} width100`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: quotationInfo?.country === "Japan" ? "30px" : undefined }}>
                  <div>
                    <div style={{ marginBottom: "20px" }}>
                      <img
                        src="/microsoft-logo.png"
                        className="invlogo"
                        style={{ width: "140px" }}
                        alt="Microsoft logo"
                      />
                    </div>
                    <div>
                      {quotationInfo?.country === "Japan" ? (
                        <>
                          <p className="pb-1">Microsoft Japan Co., Ltd.</p>
                          <p>Shinagawa Grand Central Tower</p>
                          <p className="pb-1">2-16-3 Konan, Minato-ku</p>
                          <p className="pb-1">Tokyo 108-0075, Japan</p>
                        </>
                      ) : (
                        <>
                          <p className="pb-1">
                            {countryProfileInfo?.administrativeOriginName}
                          </p>
                          <p>{countryProfileInfo?.administrativeOriginStreet}</p>
                          <p className="pb-1">
                            {countryProfileInfo?.administrativeOriginCity}
                            {countryProfileInfo?.administrativeOriginState ? `, ${countryProfileInfo.administrativeOriginState} ` : " "}
                            {countryProfileInfo?.administrativeOriginCountry === SINGAPORE_COUNTRY ? "" : countryProfileInfo?.administrativeOriginZipCode}</p>
                          <p className="pb-1">
                            {countryProfileInfo?.administrativeOriginCountry}
                            {countryProfileInfo?.administrativeOriginCountry === SINGAPORE_COUNTRY ? ` ${countryProfileInfo?.administrativeOriginZipCode}` : ""}
                          </p>
                        </>
                      )}
                      {quotationInfo?.country === SINGAPORE_COUNTRY && (
                        <p className="pb-1" style={{ marginTop: "10px" }}>
                          GST Registration Number: M9-0002526-N
                        </p>
                      )}
                      {quotationInfo?.country === "Japan" && (
                        <p className="pb-1" style={{ marginTop: "10px" }}>
                          <b>TAX ID:</b> T2010401092245
                        </p>
                      )}
                    </div>
                  </div>
                  {quotationInfo?.country === "Japan" && (
                    <div style={{ paddingTop: "50px" }}>
                      <img
                        src="/Japan-stamp.png"
                        style={{ width: "80px", height: "80px" }}
                        alt="Japan Stamp"
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "10px" }}>
                  <div>
                    <h1
                      className="pb-1"
                      style={{
                        textAlign: "right",
                        fontSize: "24px",
                      }}
                    >
                      Quotation
                    </h1>
                  </div>
                  <div>
                    <p >
                      <b>Quotation ID:&nbsp;</b>
                      {quotationInfo?.quotationNumber}
                    </p>
                    <p >
                      <b>Effective From:&nbsp;</b>
                      {formattedQuotationDate || quotationInfo?.quotationDate}
                    </p>
                    <p >
                      <b>Effective To:&nbsp;</b>
                      {formattedExpiryDate || quotationInfo?.expiryDate}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <div className="flexWrap">
                    <p>
                      <b>Company Name:</b> {` ${quotationInfo?.customer?.name}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>Account Number:</b>
                      {` ${quotationInfo?.customer?.accountId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    {quotationInfo?.customer?.region === "EMEA" && (
                      <p>
                        <b>
                          Customer{" "}
                          {lineItems.length > 0 && lineItems[0]?.taxType !== undefined
                            ? lineItems[0]?.taxType
                            : "Tax"}{" "}
                          ID:
                        </b>
                        {quotationInfo?.customer?.taxId ? ` ${quotationInfo.customer.taxId}` : ''}
                      </p>
                    )}
                  </div>
                   <div style={{ display: "flex" }}>
                      <p>
                        <b>Contact Name:</b>{' '}
                        {customerPrimaryContactName || 'N/A'}
                      </p>
                    </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>Contact Email:</b>
                      {` ${quotationInfo?.customer?.authorizedEmailId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>Phone:</b>
                      {` ${quotationInfo?.customer?.authorizedBuyerPhone}`}
                    </p>
                  </div>
                  <div style={{ display: "flex", marginTop: "30px" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>MSFT Account Lead:</b>
                      {` ${quotationInfo?.microsoftAccountLead}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>Account Lead Email:</b>
                      {` ${quotationInfo?.microsoftAccountEmail}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>Opportunity ID:</b>
                      {` ${quotationInfo?.msxOpportunityId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>Customer PO:</b>
                      {` ${quotationInfo?.customerPurchaseOrder ? quotationInfo?.customerPurchaseOrder : ""}`}
                    </p>
                  </div>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}  ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tr>
                      <td className="td-border">
                        <p>
                          <strong>Bill To:</strong>
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.name}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.street}
                        </p>
                        <p>
                          {`${quotationInfo?.customer?.selectedBillToAddress?.city},
                        ${quotationInfo?.customer?.selectedBillToAddress?.state} 
                        ${quotationInfo?.customer?.selectedBillToAddress?.country === SINGAPORE_COUNTRY ? '' : quotationInfo?.customer?.selectedBillToAddress?.postalCode}`}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.country}
                          {quotationInfo?.customer?.selectedBillToAddress?.country === SINGAPORE_COUNTRY ? ` ${quotationInfo?.customer?.selectedBillToAddress?.postalCode}` : ''}
                        </p>
                      </td>
                    </tr>
                  </table>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''} ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tr>
                      <td className="td-border">
                        <p>
                          <strong>Ship To:</strong>
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.name}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.street}
                        </p>
                        <p>
                          {`${quotationInfo?.customer?.selectedShipToAddress?.city},
                        ${quotationInfo?.customer?.selectedShipToAddress?.state}
                        ${quotationInfo?.customer?.selectedShipToAddress?.country === SINGAPORE_COUNTRY ? '' : quotationInfo?.customer?.selectedShipToAddress?.postalCode}`}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.country}
                          {quotationInfo?.customer?.selectedShipToAddress?.country === SINGAPORE_COUNTRY ? ` ${quotationInfo?.customer?.selectedShipToAddress?.postalCode}` : ''}
                        </p>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
              {quotationInfo?.comments && (
                <div style={{ borderTop: "1px solid #c4c4c4", marginTop: "10px", alignContent: "center" }}>
                  <p>
                    <strong style={{ marginRight: "5px" }} >Comments:</strong>
                    {quotationInfo?.comments}
                  </p>
                </div>
              )}

              <div className="row top20 overFlowXAuto">
                <table className="table">
                  <tr className="background-style">
                    <th className="table-border">Product Name</th>
                    <th className="table-border">SKU</th>
                    <th className="table-border">Qty</th>
                    {lineItems[0]?.taxType ? (
                      <th className="table-border">
                        Price
                      </th>
                    ) : (
                      <th className="table-border">MSRP</th>
                    )}
                    <th className="table-border">Discount Amount</th>
                    <th className="table-border">Discount Percentage</th>
                    {lineItems[0]?.taxType ? (
                      <th className="table-border">
                        Unit Price
                      </th>
                    ) : (
                      <th className="table-border">Unit Price</th>
                    )}
                    {quotationInfo?.customer?.region?.toUpperCase() === "APAC" && (
                      <th className="table-border">
                        Extended Discount Amount
                      </th>
                    )}
                    {quotationInfo?.customer?.region === "EMEA" && (
                      <th className="table-border">
                        Extended Copyright Levy Fee{quotationInfo?.totalFee && Number(quotationInfo.totalFee) > 0 ? '*' : ''}
                      </th>
                    )}
                    {quotationInfo?.customer?.region !== "APAC" && (
                      <th className="table-border">
                        Extended Line {lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'}
                      </th>
                    )}
                    <th className="table-border" style={{ textAlign: "center" }}>
                      <p>Line Total</p>
                    </th>
                  </tr>
                  {lineItems?.map((product, index) => (
                    <tr key={index}>
                      <td className="table-border">{product.productTitle}</td>
                      <td className="table-border">{product.sapItemId}</td>
                      <td className="table-border">{product.quantity}</td>
                      <td className="text-right table-border">
                        {getFormattedPrice(product?.msrp, quotationInfo?.country)}
                      </td>
                      <td className="text-right table-border">
                        {getFormattedPrice((product?.discountedPrice) / product.quantity, quotationInfo?.country)}
                      </td>
                      <td className="text-right table-border">
                        {product?.discountedPercentage}%
                      </td>
                      <td className="text-right table-border">
                        {getFormattedPrice(product?.unitPrice, quotationInfo?.country)}
                      </td>
                      {quotationInfo?.customer?.region === "APAC" && (
                        <td className="text-right table-border">
                          {getFormattedPrice(product?.discountedPrice, quotationInfo?.country)}
                        </td>
                      )}
                      {quotationInfo?.customer?.region === "EMEA" && (
                        <td className="text-right table-border">
                          {getFormattedPrice(product?.totalLineLevyFeeAmount, quotationInfo?.country)}
                        </td>
                      )}
                      {quotationInfo?.customer?.region !== "APAC" && (
                        <td className="text-right table-border">
                          {getFormattedPrice(product?.lineItemTax, quotationInfo?.country)}
                        </td>
                      )}
                      <td className="text-right table-border">
                        {getFormattedPrice(product?.lineTotal, quotationInfo?.country)}
                      </td>
                    </tr>
                  ))}


                </table>
              </div>

              <div className="below-table">
                <div className="">
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>Shipping</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {getPriceWithCurrency(quotationInfo?.quoteCurrency, 0, quotationInfo?.country)}
                    </td>
                  </tr>
                  {quotationInfo?.customer?.region === "EMEA" && (
                    <tr>
                      <td colSpan={columnsNumber} className="tDataWithoutBorder">
                        <strong>
                          Copyright Levy Total
                        </strong>
                      </td>
                      <td colSpan={1} className="text-right tDataWithoutBorder">
                        {getPriceWithCurrency(
                          quotationInfo?.quoteCurrency,
                          quotationInfo?.totalFee,
                          quotationInfo?.country
                        )}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>
                        {["Japan", "New Zealand", "Singapore"].includes(quotationInfo?.country)
                          ? "Subtotal"
                          : "Subtotal (including copyright levy)"}
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {getPriceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.subTotal,
                        quotationInfo?.country
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>{lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Amount ({lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Rate)</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {getPriceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        (quotationInfo?.totalTax) ? quotationInfo?.totalTax : 0,
                        quotationInfo?.country
                      )}
                      {' '}
                      (
                      {(lineItems.find(item => item?.effectiveTaxRate > 0)?.effectiveTaxRate || 0) * 100}
                      %){ }
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>
                        Total Amount{quotationInfo?.country === SINGAPORE_COUNTRY ? " (inclusive of GST)" : ""}
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {getPriceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.total,
                        quotationInfo?.country
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>Currency Code Displayed</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {`${quotationInfo.quoteCurrency} ${symbolForCurrency(quotationInfo.quoteCurrency)}`}
                    </td>
                  </tr>
                </div>
              </div>
              <div className={`top60 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <strong>
                  Customer Approval Signature __________________________
                </strong>
                <span style={{ paddingRight: "10px" }}>
                  <strong style={{ paddingRight: "10px" }}>Date:</strong>
                  {formattedCurrentDate || new Date().toLocaleDateString('en-US')}
                </span>
              </div>
              <div style={{ marginTop: "20px", paddingBottom: "50px" }}>
                <p>
                  Please review the terms of the Quote that are attached. By
                  issuing a Purchase Order or paying the purchase price, you agree
                  to the Business Sales Terms attached hereto for the product.
                </p>
                {quotationInfo?.customer?.region === "EMEA" && Number(quotationInfo?.totalFee) > 0 ? (
                  <p style={{ marginTop: "15px" }}>
                    *Levy Fee is a private copying levy (also known as blank media tax or levy).
                    This is an EU government-mandatory scheme in which a special tax or levy (additional to any general sales tax)
                    is charged on purchases of recordable media.
                  </p>
                ) : null}
                {quotationInfo?.country === "France" && quotationInfo?.totalFee && Number(quotationInfo.totalFee) > 0 ? (
                  <div style={{ marginTop: "15px" }}>
                    <p>Copyright Levy Fee: {getPriceWithCurrency(quotationInfo.quoteCurrency, quotationInfo.totalFee, quotationInfo.country)}</p>
                    <p>Official information notice on private copying: <a href="https://www.culture.gouv.fr/nous-connaitre/organisation-du-ministere/commission-pour-la-remuneration-de-la-copie-privee" target="_blank" rel="noopener noreferrer">https://www.culture.gouv.fr/nous-connaitre/organisation-du-ministere/commission-pour-la-remuneration-de-la-copie-privee</a></p>
                    <p>Reimbursement/exemption of copyright levy fee for professional use: <a href="https://copiefrance.fr/fr/" target="_blank" rel="noopener noreferrer">https://copiefrance.fr/fr/</a></p>
                  </div>
                ) : quotationInfo?.country === SINGAPORE_COUNTRY ? (
                  <p style={{ marginTop: "15px", fontSize: "12px" }}>
                    <strong>To learn more, click here (<a href="https://www.microsoft.com/en-sg/store/b/business" target="_">https://www.microsoft.com/en-sg/store/b/business)</a></strong>
                  </p>
                ) : quotationInfo?.country === "New Zealand" ? (
                  <p style={{ marginTop: "15px", fontSize: "12px" }}>
                    <strong>To learn more, click here (<a href="https://www.microsoft.com/en-nz/store/b/business" target="_">https://www.microsoft.com/en-nz/store/b/business</a>)</strong>
                  </p>
                ) : null}
                {quotationInfo?.country === "Austria" && hasWarranty && (
                  <p style={{ marginTop: "15px" }}>
                    <strong>Austria Electronic (E)Consent:</strong> I hereby expressly give my consent to receive documentation and communications associated with the Microsoft Protection Plan through electronic means. I can at any time revoke such consent or provide another e-mail address to be used. Likewise, the insurer has the right to revoke its consent to e-communication. Both parties to the insurance contract still have the right to send declarations, communications and information on paper. I have the right to ask for hardcopies of any declarations or information received electronically once free of charge.
                  </p>
                )}
                {quotationInfo?.country === "Italy" && hasWarranty && (
                  <p style={{ marginTop: "15px" }}>
                    <strong>Italy Terms & Conditions (T&C) Consent:</strong> "I confirm that I have read, and I agree to the Terms and Conditions of the Microsoft Protection Plan".
                  </p>
                )}
                {/* Domestic reverse charges message for specific countries */}
                {showDomesticReverseCharges && (
                  <p style={{ marginTop: "15px" }}>
                    *Domestic reverse charges applied
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p>Record not found</p>
      )}
    </div>
  );
};
